/**
 * Clean command - prune stale task records
 *
 * Since sandbox tasks complete synchronously (exec blocks until done),
 * there are no persistent sandboxes to destroy. This command simply
 * prunes stale task records from local config.
 */

import chalk from 'chalk';
import { loadTasks, updateTask, pruneTasks } from '../config.js';

export interface CleanOptions {
  force?: boolean;
}

export async function clean(options: CleanOptions = {}): Promise<void> {
  console.log(chalk.bold('\nCleaning up stale task records...\n'));

  const allTasks = await loadTasks();

  // Find running tasks (likely stale since sandbox exec blocks until done)
  const staleTasks = allTasks.filter(t => t.status === 'running' || t.status === 'pending');

  if (staleTasks.length === 0) {
    console.log(chalk.green('No stale tasks to clean up.'));

    // Also prune old completed tasks
    const pruned = await pruneTasks();
    if (pruned > 0) {
      console.log(chalk.dim(`Pruned ${pruned} old completed task(s).`));
    }
    return;
  }

  if (!options.force) {
    console.log(chalk.yellow(`Found ${staleTasks.length} stale task(s):`));
    for (const t of staleTasks) {
      console.log(chalk.dim(`  - ${t.id} (${t.status}) ${t.repo}`));
    }
    console.log(chalk.dim('\nUse --force to mark them as completed and prune old records.'));
    return;
  }

  // Mark stale tasks as completed
  let cleaned = 0;
  for (const task of staleTasks) {
    await updateTask(task.id, {
      status: 'completed',
      completedAt: new Date().toISOString(),
    });
    console.log(chalk.dim(`  Marked ${task.id} as completed.`));
    cleaned++;
  }

  // Also prune old completed tasks
  const pruned = await pruneTasks();

  console.log('');
  console.log(chalk.green(`Cleaned ${cleaned} stale task(s).`));
  if (pruned > 0) {
    console.log(chalk.dim(`Pruned ${pruned} old completed task(s).`));
  }
}
